import { customerIce } from './customerMenu';
import { drinkProductName } from './drinkGroups';
import type { Catalog, Product, RecipeIngredient, Selections, Unit } from './domain';

export type BrewMethod = 'espresso' | 'manual';
export interface BrewingProfile {
  machine: string;
  grinder: string;
  grindSetting?: string;
  temperatureC?: number;
  targetYieldG?: number;
  timeMinSeconds?: number;
  timeMaxSeconds?: number;
  timerStart?: 'pump' | 'first_drip';
  brewByWeight?: boolean;
  machineWeightG?: number;
  notes?: string;
  updatedAt?: number;
}
export type BrewingProfiles = Partial<Record<BrewMethod, BrewingProfile>>;
export interface RecipeView {
  name: string;
  method: BrewMethod | 'none';
  ingredients: { name: string; quantity: number; unit: Unit; role?: RecipeIngredient['role']; extra?: string }[];
  brews: { bean: string; dose: number; unit: Unit; extra?: string; profile?: BrewingProfile }[];
  steps: string;
  revision: number;
}
export function defaultBrewingProfile(method: BrewMethod): BrewingProfile {
  return { machine: method === 'espresso' ? 'Venuella Alessandro 2' : 'Hario Switch', grinder: 'Guerrero GR-64' };
}
export function brewingMethod(product: Product): BrewMethod | 'none' {
  return product.brewMethod ?? (product.equipmentIds.includes('coffee_machine') || product.costingProfile === 'espresso' ? 'espresso' : product.equipmentIds.includes('switch') || product.costingProfile === 'manual' ? 'manual' : 'none');
}
// Explicit projection: preparation data never contains purchase prices or internal costs.
function safeProfile(p: BrewingProfile): BrewingProfile {
  const keys = ['machine', 'grinder', 'grindSetting', 'temperatureC', 'targetYieldG', 'timeMinSeconds', 'timeMaxSeconds', 'timerStart', 'brewByWeight', 'machineWeightG', 'notes', 'updatedAt'] as const;
  return Object.fromEntries(keys.filter(k => p[k] !== undefined).map(k => [k, p[k]])) as unknown as BrewingProfile;
}
export function resolveRecipe(catalog: Catalog, productId: string, selections: Selections): RecipeView {
  const saved = catalog.products.find(p => p.id === productId && !p.archived);
  const p = saved && customerIce(saved);
  if (!p) throw new Error('Recipe not available.');
  if (!p.iceLevels.includes(selections.ice) || !p.sweetnessLevels.includes(selections.sweetness) ||
      (p.beanIds.length ? !selections.beanId || !p.beanIds.includes(selections.beanId) : !!selections.beanId) ||
      !Array.isArray(selections.extras) || new Set(selections.extras).size !== selections.extras.length) throw new Error('Invalid recipe options.');
  const method = brewingMethod(p);
  const ingredients: RecipeView['ingredients'] = [];
  const brews: RecipeView['brews'] = [];
  const parts = p.ingredients.map(part => ({ part, extra: undefined as string | undefined }));
  for (const id of selections.extras) {
    const extra = p.extras.find(e => e.id === id);
    if (!extra) throw new Error('Invalid recipe options.');
    parts.push(...extra.ingredients.map(part => ({ part, extra: extra.name })));
  }
  for (const { part, extra } of parts) {
    const id = part.role === 'bean' ? selections.beanId : part.ingredientId;
    const ingredient = catalog.ingredients.find(i => i.id === id && !i.archived);
    if (!ingredient) throw new Error('Recipe ingredient not available.');
    let quantity = part.quantity;
    if (part.role === 'sweetener') quantity *= selections.sweetness / 100;
    if (part.role === 'ice') quantity = p.iceQuantities[selections.ice] ?? 0;
    quantity = Math.round(quantity * 1e6) / 1e6;
    if (quantity === 0) continue;
    ingredients.push({ name: ingredient.name, quantity, unit: part.unit, ...(part.role ? { role: part.role } : {}), ...(extra ? { extra } : {}) });
    if (method !== 'none' && (part.role === 'bean' || ingredient.brewingProfiles?.[method])) {
      const profile = ingredient.brewingProfiles?.[method];
      brews.push({ bean: ingredient.name, dose: quantity, unit: part.unit, ...(extra ? { extra } : {}), ...(profile ? { profile: safeProfile(profile) } : {}) });
    }
  }
  return { name: drinkProductName(catalog, p), method, ingredients, brews, steps: p.preparationSteps ?? '', revision: catalog.revision };
}
export function validatePreparation(catalog: Catalog): string[] {
  const errors: string[] = [];
  for (const p of catalog.products) {
    if (p.brewMethod !== undefined && !['espresso', 'manual', 'none'].includes(p.brewMethod)) errors.push(`${p.name}: invalid brew method.`);
    if (p.preparationSteps !== undefined && (typeof p.preparationSteps !== 'string' || p.preparationSteps.length > 6000)) errors.push(`${p.name}: preparation steps must be at most 6000 characters.`);
  }
  for (const bean of catalog.ingredients) {
    if (bean.brewingProfiles === undefined) continue;
    const fail = (message: string) => errors.push(`${bean.name}: ${message}`);
    if (!bean.brewingProfiles || typeof bean.brewingProfiles !== 'object' || Array.isArray(bean.brewingProfiles)) { fail('invalid brewing profiles.'); continue; }
    for (const [method, p] of Object.entries(bean.brewingProfiles)) {
      if (!['espresso', 'manual'].includes(method) || !p || typeof p !== 'object') { fail('invalid brewing profile.'); continue; }
      for (const key of ['machine', 'grinder', 'grindSetting', 'notes'] as const) {
        if (p[key] !== undefined && (typeof p[key] !== 'string' || p[key]!.length > (key === 'notes' ? 6000 : 200))) fail(`invalid ${key}.`);
      }
      for (const key of ['temperatureC', 'targetYieldG', 'timeMinSeconds', 'timeMaxSeconds', 'machineWeightG'] as const) {
        const value = p[key];
        if (value !== undefined && (typeof value !== 'number' || !Number.isFinite(value) || value <= 0 || value > (key === 'temperatureC' ? 150 : 10000))) fail(`invalid ${key}.`);
      }
      if ((p.timeMinSeconds === undefined) !== (p.timeMaxSeconds === undefined) || (p.timeMinSeconds !== undefined && p.timeMaxSeconds !== undefined && p.timeMinSeconds > p.timeMaxSeconds)) fail('enter a valid minimum and maximum brew time.');
      if (p.timerStart !== undefined && !['pump', 'first_drip'].includes(p.timerStart)) fail('invalid timer reference.');
      if (p.brewByWeight !== undefined && typeof p.brewByWeight !== 'boolean') fail('invalid brew-by-weight mode.');
      if (p.brewByWeight && p.machineWeightG === undefined) fail('enter the machine brew-by-weight setting.');
      if (method === 'manual' && (p.brewByWeight !== undefined || p.machineWeightG !== undefined || p.timerStart !== undefined)) fail('espresso controls cannot be used for manual brewing.');
    }
  }
  return errors;
}
